"use client";

import { useCallback, useEffect, useState } from "react";

const SEEN_STORAGE_KEY = "flots-exit-intent-seen";

/**
 * How long a visitor is left alone after seeing the offer. The cap lives in
 * localStorage rather than sessionStorage: sessionStorage is scoped to a single
 * tab, so opening the same article in three tabs handed out three popups.
 */
const SEEN_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Brief grace period before exit detection starts. Eight seconds missed the
 * short visits this offer is specifically meant to retain.
 */
const ARM_DELAY_MS = 1500;

/**
 * Detect the deliberate upward movement before the browser stops forwarding
 * pointer events. Requiring an exact `clientY <= 0` made the trigger depend on
 * browser chrome and window-manager details.
 */
const TOP_EXIT_ZONE_PX = 8;

/**
 * Beyond this gap the previous pointer sample no longer describes a continuous
 * gesture. Real movement is sampled every few milliseconds; the mouse events
 * Chromium synthesises on its own arrive long after the visitor last moved.
 */
const POINTER_SAMPLE_MAX_GAP_MS = 300;

export type ExitIntentTrigger = "mouse-leave";

type ExitIntentState = {
  isOpen: boolean;
  trigger: ExitIntentTrigger | null;
};

/**
 * True when a dialog is currently on screen. Some dialogs (the mobile nav drawer)
 * stay mounted and are only slid out of the viewport, so presence in the DOM
 * isn't enough — the box has to actually intersect the viewport.
 */
function hasVisibleDialog(): boolean {
  const dialogs = document.querySelectorAll<HTMLElement>('[role="dialog"]');
  return Array.from(dialogs).some((el) => {
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return false;
    return (
      rect.right > 0 &&
      rect.left < window.innerWidth &&
      rect.bottom > 0 &&
      rect.top < window.innerHeight
    );
  });
}

function wasSeenRecently(): boolean {
  try {
    // Key written by the first release, when the cap was per tab. Keep honouring
    // it so a visitor mid-session doesn't get the popup a second time.
    if (sessionStorage.getItem(SEEN_STORAGE_KEY)) return true;
  } catch {
    // sessionStorage unavailable — localStorage below is the real cap anyway.
  }

  try {
    const seenAt = Number(localStorage.getItem(SEEN_STORAGE_KEY));
    return Number.isFinite(seenAt) && seenAt > 0 && Date.now() - seenAt < SEEN_TTL_MS;
  } catch {
    // Storage blocked entirely (private mode, cookies off). Staying silent beats
    // showing the offer on every single page load.
    return true;
  }
}

function markSeen(): void {
  const now = String(Date.now());
  try {
    localStorage.setItem(SEEN_STORAGE_KEY, now);
  } catch {
    // Nothing to do — the in-memory guard still holds for this page.
  }
  try {
    sessionStorage.setItem(SEEN_STORAGE_KEY, now);
  } catch {
    // Same.
  }
}

/**
 * Detects that the visitor is about to leave the page: the cursor moves upward
 * into the top edge or exits through it, heading for the tab bar / close button.
 *
 * Every trigger requires a continuous pointer trail that starts inside the page,
 * because a bare `mouseout` with a null `relatedTarget` is not proof of anything:
 * Chromium emits those on tab activation, on window focus changes and when it
 * re-runs hit testing after a layout shift. Those synthetic events carry the
 * pointer's current position — or `0, 0` on a document the pointer never
 * visited — which used to land inside the exit zone and open the popup by itself.
 *
 * Tab visibility is deliberately not a trigger. Switching tabs, opening a new
 * one or alt-tabbing away is ordinary browsing, not an exit, and firing on it
 * opened the popup behind the visitor's back.
 *
 * Fires at most once per visitor per SEEN_TTL_MS, across tabs.
 */
export function useExitIntent() {
  const [state, setState] = useState<ExitIntentState>({
    isOpen: false,
    trigger: null,
  });

  useEffect(() => {
    if (wasSeenRecently()) return;

    let isArmed = false;
    const armTimeout = setTimeout(() => {
      isArmed = true;
    }, ARM_DELAY_MS);

    /** Last pointer position genuinely observed inside the document. */
    let lastMouseY: number | null = null;
    let lastMouseAt = 0;

    const fire = (trigger: ExitIntentTrigger) => {
      if (!isArmed) return;
      // Another dialog already has the visitor's attention — don't stack popups.
      if (hasVisibleDialog()) return;

      isArmed = false;
      markSeen();
      setState({ isOpen: true, trigger });
    };

    const handleMouseMove = (event: MouseEvent) => {
      const previousMouseY = lastMouseY;
      const previousMouseAt = lastMouseAt;
      lastMouseY = event.clientY;
      lastMouseAt = event.timeStamp;

      if (previousMouseY === null) return;
      // A jump with no recent sample before it is a synthetic event, not a gesture.
      if (event.timeStamp - previousMouseAt > POINTER_SAMPLE_MAX_GAP_MS) return;

      const enteredExitZone =
        previousMouseY > TOP_EXIT_ZONE_PX &&
        event.clientY <= TOP_EXIT_ZONE_PX &&
        event.clientY < previousMouseY;

      if (enteredExitZone) fire("mouse-leave");
    };

    const handleMouseOut = (event: MouseEvent) => {
      // relatedTarget is null only when the cursor leaves the document itself
      if (event.relatedTarget) return;
      // Only the top edge means "going for the browser chrome"
      if (event.clientY > TOP_EXIT_ZONE_PX) return;
      // The cursor has to have been inside the page, below the exit zone, and it
      // has to have been there just now. Without this, Chromium's own boundary
      // events are enough to open the popup.
      if (lastMouseY === null || lastMouseY <= TOP_EXIT_ZONE_PX) return;
      if (event.timeStamp - lastMouseAt > POINTER_SAMPLE_MAX_GAP_MS) return;

      fire("mouse-leave");
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseout", handleMouseOut);

    return () => {
      clearTimeout(armTimeout);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseout", handleMouseOut);
    };
  }, []);

  const close = useCallback(
    () => setState((current) => ({ ...current, isOpen: false })),
    [],
  );

  return { isOpen: state.isOpen, trigger: state.trigger, close };
}
